<template>
  <nav class="navbar">
    <div class="container-fluid">
      <ol class="breadcrumb mb-0" aria-label="breadcrumb" data-v-step="2">
        <template v-if="breadcrumb">
          <li class="breadcrumb-item">
            <router-link
              class="back-icon"
              :to="{
                name: 'parent-list',
                params: { account: $route.params.account }
              }"
            >
              <LeftChevron class="d-md-none" />
              <div class="d-none d-md-flex align-items-center">
                <figure class="logo d-flex align-items-center m-0 me-3" alt="PTC Wizard Logo">
                  <Logo class="h-75" />
                </figure>
                <span class="d-inline-block" v-t="'message.Navbar.my_meetings'"></span>
              </div>
            </router-link>
          </li>
          <li class="breadcrumb-item active" aria-current="page">
            {{ breadcrumb }}
          </li>
        </template>
        <template v-else>
          <li class="breadcrumb-item active" aria-current="page">
            <figure class="logo d-flex align-items-center m-0 me-3" alt="PTC Wizard Logo">
              <Logo class="h-75" />
            </figure>
            <span class="d-none d-md-inline-block" v-t="'message.Navbar.my_meetings'"></span>
          </li>
        </template>
      </ol>
      
      <!-- Language Dropdown -->
      <div class="dropdown">
        <button class="btn text-white dropdown-toggle text-capitalize fs-normal px-2"
          type="button"
          id="dropdownLanguageButton"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          <component :is="currentLanguageFlag"></component>
          {{ currentLanguage.code.toUpperCase() }}
        </button>
        <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="dropdownLanguageButton">
          <li v-for="language in supportedLanguages" :key="language.code">
            <span 
              class="dropdown-item" 
              @click="changeLocale(language.code)" 
              :class="{ active: $i18n.locale === language.code }"
            >
              <component :is="getFlagComponent(language.flag)"></component>
              &nbsp; {{ language.nativeName }}
            </span>
          </li>
        </ul>
      </div>
      
      <button class="help fs-6" @click="startTourManually()">
        <i class="bi fs-5 bi-question-circle"></i>
        <span v-t="'message.Navbar.help'"></span>
      </button>
      <div class="dropdown">
        <button
          class="btn text-white dropdown-toggle text-capitalize fs-normal px-2"
          type="button"
          id="dropdownMenuButton1"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          <i class="bi bi-person-fill fs-4 me-n1 d-md-none"></i>
          <span class="d-none d-md-inline-block align-middle"
            >{{ authenticationStore.me.firstname }} {{ authenticationStore.me.lastname }}</span
          >
        </button>
        <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="dropdownMenuButton1">
          <li>
            <span class="dropdown-item" @click="authenticationStore.modal = true">
              <span v-t="'message.Navbar.account_settings'"></span>
            </span
            >
          </li>
          <li>
            <span class="dropdown-item" @click="logout()">
            <span v-t="'message.Navbar.logout'"></span>
            </span>
          </li>
        </ul>
      </div>
    </div>
  </nav>
  <ModalAccountSettings />

  <PtcTour ref="tour" :steps="getSteps($i18n.locale)" @tour-closed="onTourClosed"></PtcTour>
</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";
import ModalAccountSettings from "@/components/parent/modal/AccountSettings.vue";
import { useAuthenticationStore } from "@/stores/authentication";
import { useAgendaStore } from "@/stores/parent/agenda";
import { useRoute } from "vue-router";
import { useSchoolStore } from "@/stores/school";
import Logo from "@/assets/icons/logo-vertical-knockout.svg";
import LeftChevron from "@/assets/icons/left-chevron.svg";

// Flag imports
import FlagUS from "@/assets/icons/flags/usa.svg";
import FlagES from "@/assets/icons/flags/es.svg";
import FlagFR from "@/assets/icons/flags/fr.svg";
import FlagDE from "@/assets/icons/flags/de.svg";
import FlagAR from "@/assets/icons/flags/ar.svg";
import FlagCN from "@/assets/icons/flags/cn.svg";
import FlagJP from "@/assets/icons/flags/jp.svg";
import FlagBD from "@/assets/icons/flags/bd.svg";
import FlagKR from "@/assets/icons/flags/kr.svg";
import FlagPK from "@/assets/icons/flags/pk.svg";
import FlagSN from "@/assets/icons/flags/sn.svg";
import FlagRU from "@/assets/icons/flags/ru.svg";
import FlagUA from "@/assets/icons/flags/ua.svg";
import FlagUZ from "@/assets/icons/flags/uz.svg";
import FlagGE from "@/assets/icons/flags/ge.svg";
import FlagHK from "@/assets/icons/flags/hk.svg";
import FlagHT from "@/assets/icons/flags/ht.svg";

import PtcTour from "@/components/parent/PtcTour.vue";
import { isLink } from "@/utils";
import { useLanguage } from "@/composables/useLanguage.js";

const route = useRoute();
const authenticationStore = useAuthenticationStore();
const schoolStore = useSchoolStore();
const agendaStore = useAgendaStore();
const { currentLanguage, supportedLanguages, changeLocale } = useLanguage();

const tour = ref(null);
const tourManuallyClosed = ref(false);

// Flag component mapping
const flagComponents = {
  'usa': FlagUS,
  'es': FlagES,
  'fr': FlagFR,
  'de': FlagDE,
  'ar': FlagAR,
  'cn': FlagCN,
  'jp': FlagJP,
  'bd': FlagBD,
  'kr': FlagKR,
  'pk': FlagPK,
  'sn': FlagSN,
  'ru': FlagRU,
  'ua': FlagUA,
  'uz': FlagUZ,
  'ge': FlagGE,
  'hk': FlagHK,
  'ht': FlagHT
};

const currentLanguageFlag = computed(() => {
  return flagComponents[currentLanguage.value.flag] || FlagUS;
});

function getFlagComponent(flagKey) {
  return flagComponents[flagKey] || FlagUS;
}

function getSteps(locale) {
  console.log(route.name);
  if (tour.value && route.name == "parent-matrix") {
    // Add support for more languages as tour translations become available
    switch(locale) {
      case 'es':
        return tour.value.matrixStepsSpanish;
      case 'fr':
        return tour.value.matrixStepsFrench || tour.value.matrixSteps;
      case 'de':
        return tour.value.matrixStepsGerman || tour.value.matrixSteps;
      case 'ar':
        return tour.value.matrixStepsArabic || tour.value.matrixSteps;
      case 'zh':
      case 'zh-cn':
        return tour.value.matrixStepsChinese || tour.value.matrixSteps;
      case 'ja':
        return tour.value.matrixStepsJapanese || tour.value.matrixSteps;
      case 'bn':
        return tour.value.matrixStepsBengali || tour.value.matrixSteps;
      case 'ru':
        return tour.value.matrixStepsRussian || tour.value.matrixSteps;
      case 'uk':
        return tour.value.matrixStepsUkrainian || tour.value.matrixSteps;
      case 'uz':
        return tour.value.matrixStepsUzbek || tour.value.matrixSteps;
      case 'ka':
        return tour.value.matrixStepsGeorgian || tour.value.matrixSteps;
      case 'yue':
        return tour.value.matrixStepsCantonese || tour.value.matrixSteps;
      case 'ht':
        return tour.value.matrixStepsHaitianCreole || tour.value.matrixSteps;
      default:
        return tour.value.matrixSteps;
    }
  } else if (tour.value && route.name == "parent-list") {
    switch(locale) {
      case 'es':
        return tour.value.agendaStepsSpanish;
      case 'fr':
        return tour.value.agendaStepsFrench || tour.value.agendaSteps;
      case 'de':
        return tour.value.agendaStepsGerman || tour.value.agendaSteps;
      case 'ar':
        return tour.value.agendaStepsArabic || tour.value.agendaSteps;
      case 'zh':
      case 'zh-cn':
        return tour.value.agendaStepsChinese || tour.value.agendaSteps;
      case 'ja':
        return tour.value.agendaStepsJapanese || tour.value.agendaSteps;
      case 'bn':
        return tour.value.agendaStepsBengali || tour.value.agendaSteps;
      case 'ru':
        return tour.value.agendaStepsRussian || tour.value.agendaSteps;
      case 'uk':
        return tour.value.agendaStepsUkrainian || tour.value.agendaSteps;
      case 'uz':
        return tour.value.agendaStepsUzbek || tour.value.agendaSteps;
      case 'ka':
        return tour.value.agendaStepsGeorgian || tour.value.agendaSteps;
      case 'yue':
        return tour.value.agendaStepsCantonese || tour.value.agendaSteps;
      case 'ht':
        return tour.value.agendaStepsHaitianCreole || tour.value.agendaSteps;
      default:
        return tour.value.agendaSteps;
    }
  }
}

function onTourClosed() {
  console.log('Tour was manually closed');
  tourManuallyClosed.value = true;
}

function startTourManually() {
  tourManuallyClosed.value = false;
  tour.value.start();
}

defineProps(["breadcrumb"]);

function logout() {
  authenticationStore.logout();
  if (schoolStore.school?.school_url && isLink(schoolStore.school.school_url)) {
    document.location.href = schoolStore.school.school_url;
  } else {
    location.pathname = "/" + route.params.account + "/login";
  }
}

watch(
  () => agendaStore.loading,
  async (agendaLoading) => {
    if (!agendaLoading) {
      if (agendaStore.agenda.length == 0 && !authenticationStore.modal && !tourManuallyClosed.value) {
        tour.value.start();
      }
    }
  }
);

onMounted(() => {
  try {
    if (schoolStore.school == null) {
      schoolStore.fetchSchool().then(() => {
        console.log("got school data again");
      });
    }
  } catch (e) {
    console.log(e);
  }
});
</script>

<style scoped>
.dropdown-item {
  cursor: pointer;
  display: flex;
  align-items: center;
}

.dropdown-item.active {
  background-color: #007bff;
  color: white;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item.active:hover {
  background-color: #0056b3;
}
</style>
